"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { logger } from "@/utils/logger";
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  FileText,
  Info,
  Link2,
  Pencil,
  BarChart3,
  LayoutGrid,
  Dot,
  SquareArrowOutUpRight,
} from "lucide-react";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";

// --- Interfaces ---
/**
 * Frontend interface corresponding to backend's DetailedSuggestionSchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents a specific content update suggestion with original and updated text sections
 */
interface DetailedSuggestionSchema {
  original_section?: string | null;
  updated_section?: string | null;
}

/**
 * Frontend interface corresponding to backend's ReportEntrySchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents a single analysis entry comparing a release item to an article
 */
interface ReportEntrySchema {
  id: string;
  query_id: string;
  update_needed: boolean | null;
  relevance_score: number | null;
  reasoning?: string | null;
  general_suggestions: string[];
  detailed_suggestions: DetailedSuggestionSchema[];
  similarity_score: number | null;
  article_id: string;
  article_title: string;
  article_url?: string | null;
  article_description?: string | null;
  article_categories: string[];
  release_note_id: string;
  release_note_title: string;
  release_note_version?: string | null;
  release_note_url?: string | null;
  release_item_title: string;
  release_item_section_title?: string | null;
  release_item_version?: string | null;
  release_item_url?: string | null;
  release_item_description?: string | null;
  release_item_categories: string[];
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
}

/**
 * Frontend interface corresponding to backend's ReleaseItemGroupSchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents a group of entries for a specific release item within a release note
 */
interface ReleaseItemGroupSchema {
  item_title: string;
  item_version?: string | null;
  item_url?: string | null;
  item_description?: string | null;
  item_section_title?: string | null;
  item_categories: string[];
  updates_needed_count?: number;
  entries: ReportEntrySchema[];
}

interface UserInfo {
  username: string;
  name?: string | null;
  avatar_url?: string | null;
}

interface AssignmentInfo {
  assigned_to: UserInfo;
  assigned_by: UserInfo;
  assigned_at: string;
}

/**
 * Frontend interface corresponding to backend's ReleaseNoteDetailSchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents complete details for a specific release note, including all release items
 * Returned by GET /api/release-note/{note_id}/details endpoint
 */

interface ReleaseNoteDetailSchema {
  id: string;
  title: string;
  version?: string | null;
  url?: string | null;
  items: ReleaseItemGroupSchema[];
  assigned_to?: string | null;
  assigned_user_info?: AssignmentInfo | null;
}

// Rename props interface
interface ReleaseNoteProps {
  apiBaseUrl: string;
}

// Rename component function and props interface type
const ReleaseNote: React.FC<ReleaseNoteProps> = ({ apiBaseUrl }) => {
  const { noteId } = useParams<{ noteId: string }>();
  const [noteDetails, setNoteDetails] =
    useState<ReleaseNoteDetailSchema | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();

  // --- Fetch Details ---
  const fetchDetails = useCallback(async () => {
    if (!noteId) {
      setError("Release Note ID not found in URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<ReleaseNoteDetailSchema>(
        `${apiBaseUrl}/release-note/${noteId}/details`
      );
      setNoteDetails(response.data);
    } catch (err) {
      logger.error(`Error fetching details for note ${noteId}`, {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/release-note/${noteId}/details`,
        noteId,
      });
      let errorMsg = `Failed to load details for Release Note ${noteId}.`;
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 404) {
          errorMsg = `Release Note with ID '${noteId}' not found.`;
        } else {
          errorMsg = `API Error: ${err.response.data?.detail || err.message}`;
        }
      } else if (err instanceof Error) {
        errorMsg = `Error: ${err.message}`;
      }
      setError(errorMsg);
      setNoteDetails(null);
    } finally {
      setLoading(false);
    }
  }, [noteId, apiBaseUrl]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // --- Set Breadcrumbs ---
  useEffect(() => {
    if (noteDetails) {
      setBreadcrumbs([
        { label: "Release Notes", href: "/release-notes" },
        {
          label: noteDetails.version
            ? `${noteDetails.version}`
            : noteDetails.title || "Details",
        },
      ]);
    } else if (loading) {
      setBreadcrumbs([
        { label: "Release Notes", href: "/release-notes" },
        { label: "Loading..." },
      ]);
    } else {
      setBreadcrumbs([
        { label: "Release Notes", href: "/release-notes" },
        { label: "Error Loading Details" },
      ]);
    }
  }, [setBreadcrumbs, noteDetails, loading]);

  // Calculate summary statistics
  const calculateStats = () => {
    if (!noteDetails) return null;

    let totalEntries = 0;
    let updatesNeeded = 0;
    let totalRelevanceScore = 0;
    let totalSimilarityScore = 0;
    let relevanceCount = 0;
    let similarityCount = 0;
    let totalTokens = 0;

    noteDetails.items.forEach((item) => {
      totalEntries += item.entries.length;

      item.entries.forEach((entry) => {
        if (entry.update_needed === true) updatesNeeded++;

        if (entry.relevance_score !== null) {
          totalRelevanceScore += entry.relevance_score;
          relevanceCount++;
        }

        if (entry.similarity_score !== null) {
          totalSimilarityScore += entry.similarity_score;
          similarityCount++;
        }

        if (entry.total_tokens) {
          totalTokens += entry.total_tokens;
        }
      });
    });

    const avgRelevance =
      relevanceCount > 0 ? totalRelevanceScore / relevanceCount : null;
    const avgSimilarity =
      similarityCount > 0 ? totalSimilarityScore / similarityCount : null;
    const updatePercentage =
      totalEntries > 0 ? (updatesNeeded / totalEntries) * 100 : 0;

    return {
      totalEntries,
      updatesNeeded,
      avgRelevance,
      avgSimilarity,
      updatePercentage,
      totalTokens,
    };
  };

  const stats = calculateStats();

  // --- Loading States ---
  if (loading) {
    return (
      <div className="max-w-8xl mx-auto space-y-6 p-4 lg:p-6">
        {/* Header Skeleton */}
        <div className="card bg-base-200 shadow-md">
          <div className="card-body">
            <div className="skeleton h-8 w-3/4 mb-3"></div>
            <div className="skeleton h-4 w-1/4"></div>
            <div className="skeleton h-10 w-48 mt-4"></div>
          </div>
        </div>

        {/* Item Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-64 w-full rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  // --- Error States ---
  if (error) {
    return (
      <div role="alert" className="alert alert-error max-w-3xl mx-auto mt-8 shadow-lg">
        <AlertTriangle className="h-6 w-6" />
        <div>
          <h3 className="font-bold">Error</h3>
          <div className="text-xs">{error}</div>
        </div>
        <Link to="/release-notes" className="btn btn-sm btn-outline">
          <ArrowLeft className="h-4 w-4" />
          Back to List
        </Link>
      </div>
    );
  }

  if (!noteDetails) {
    return (
      <div className="card bg-base-200 shadow-md max-w-3xl mx-auto mt-8">
        <div className="card-body items-center text-center">
          <p className="text-base-content/70">
            Release note details could not be loaded.
          </p>
          <div className="card-actions justify-end mt-4">
            <Link to="/release-notes" className="btn btn-sm btn-outline">
              <ArrowLeft className="h-4 w-4" />
              Back to List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto flex flex-col md:flex-row gap-6 p-4 lg:p-6">
      {/* --- Left Column --- */}
      <div className="w-full md:w-2/3 space-y-6">
        
        {/* --- Release Note Header --- */}
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <h2 className="text-2xl font-semibold flex items-center py-1 gap-2">
              <LayoutGrid className="h-6 w-6 text-primary/80" />
              {noteDetails.title}
            </h2>
            {noteDetails.assigned_user_info && (
              <div className="badge badge-info gap-1 text-xs text-info-content">
                {noteDetails.assigned_user_info.assigned_to.name}
              </div>
            )}
          </div>
          {noteDetails.url && (
            <div className="mb-3">
              <a
                href={noteDetails.url}
                target="_blank"
                rel="noopener noreferrer"
                className="link link-hover link-info inline-flex items-center text-sm"
              >
                View Original Release Note{" "}
                <SquareArrowOutUpRight className="ml-1 h-3 w-3" />
              </a>
            </div>
          )}

          {/* --- Release Items Table --- */}
          <div className="card bg-base-200 shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-fixed w-full table-zebra table-pin-rows">
                <thead>
                  <tr>
                    <th className="w-[45%] pl-4 lg:pl-6">Title</th>
                    <th className="w-[15%] text-center">Analysed</th>
                    <th className="w-[15%] text-center">Outdated</th>
                    <th className="w-[25%] pr-4 lg:pr-6">Section</th>
                  </tr>
                </thead>
                <tbody>
                  {noteDetails.items.map((item, index) => {
                    const updatesNeededCount = item.entries.filter(
                      (e) => e.update_needed === true
                    ).length;
                    const itemDetailUrl = `/release-notes/${noteId}/item/${encodeURIComponent(
                      item.item_title
                    )}`;

                    const descriptionWithoutImages = item.item_description
                      ? item.item_description.replace(/<img[^>]*>/g, "")
                      : null;

                    return (
                      <tr
                        key={`item-row-${index}`}
                        className={`cursor-pointer ${
                          updatesNeededCount === 0 ? 'bg-success/5 hover:bg-success/10' : 'hover:bg-base-300'
                        }`}
                        onClick={() => navigate(itemDetailUrl)}
                      >
                        <td className="font-medium pl-4 lg:pl-6 py-3 align-top">
                          <Link
                            to={itemDetailUrl}
                            className="link link-hover text-primary flex items-center gap-2"
                          >
                            <FileText className="h-4 w-4 text-primary/70 flex-shrink-0" />
                            <span className="flex-grow line-clamp-2">
                              {item.item_title}
                            </span>
                          </Link>
                          {descriptionWithoutImages && (
                            <div
                              className="mt-1 text-xs text-base-content/70 line-clamp-2 overflow-hidden break-words"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  descriptionWithoutImages
                                ),
                              }}
                            />
                          )}
                        </td>
                        <td className="text-center align-top py-3">
                          <div className="badge badge-neutral badge-sm">
                            {item.entries.length}
                          </div>
                        </td>
                        <td className="text-center align-top py-3">
                          {updatesNeededCount > 0 ? (
                            <div className="badge badge-error badge-sm gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {updatesNeededCount}
                            </div>
                          ) : (
                            <div className="badge badge-success badge-sm gap-1">
                              <CheckCircle className="h-3 w-3" /> 0
                            </div>
                          )}
                        </td>
                        <td className="align-top py-3 pr-4 lg:pr-6 text-xs text-base-content/70 line-clamp-2">
                          {item.item_section_title || <span className="italic">N/A</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {noteDetails.items.length === 0 && (
              <div className="card-body items-center text-center p-4">
                <p className="text-base-content/70">
                  No release items found in this note.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* End Release Items Grid Container */}

      {/* --- Right Column --- */}
      <div className="w-full md:w-1/3 space-y-6 flex-shrink-0">
        {/* Overall Statistics */}
        {stats && (
          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary/80" />
                Overall Statistics
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="card card-compact bg-base-100 shadow-sm">
                  <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-base-content/70">
                          Articles Analyzed
                        </p>
                        <h4 className="text-2xl font-bold mt-1">
                          {stats.totalEntries}
                        </h4>
                      </div>
                      <FileText className="h-8 w-8 text-primary/40" />
                    </div>
                  </div>
                </div>

                <div className="card card-compact bg-base-100 shadow-sm">
                  <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-base-content/70">
                          Updates Needed
                        </p>
                        <h4 className="text-2xl font-bold mt-1">
                          {stats.updatesNeeded}
                        </h4>
                      </div>
                      <Pencil className="h-8 w-8 text-warning/40" />
                    </div>
                    <progress
                      className="progress progress-warning h-1.5 mt-2"
                      value={stats.updatePercentage}
                      max="100"
                    ></progress>
                  </div>
                </div>

                <div className="card card-compact bg-base-100 shadow-sm">
                  <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-base-content/70">
                          Avg. Relevance
                        </p>
                        <h4 className="text-2xl font-bold mt-1">
                          {stats.avgRelevance !== null
                            ? stats.avgRelevance.toFixed(2)
                            : "N/A"}
                        </h4>
                      </div>
                      <BarChart3 className="h-8 w-8 text-info/40" />
                    </div>
                  </div>
                </div>

                <div className="card card-compact bg-base-100 shadow-sm">
                  <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-base-content/70">
                          Total Tokens
                        </p>
                        <h4 className="text-2xl font-bold mt-1">
                          {stats.totalTokens.toLocaleString()}
                        </h4>
                      </div>
                      <Info className="h-8 w-8 text-success/40" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- Updates Required Summary --- */}
        {noteDetails.items.flatMap((item) =>
          item.entries.filter((entry) => entry.update_needed === true)
        ).length > 0 && (
          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                Updates Required Summary
              </h2>
              <p className="text-base-content/70 text-sm mt-1 mb-3">
                Articles needing updates based on this release note.
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {noteDetails.items.map((item, itemIndex) => {
                  const needsUpdateEntries = item.entries.filter(
                    (entry) => entry.update_needed === true
                  );
                  if (needsUpdateEntries.length === 0) return null;

                  return (
                    <div key={`update-collapse-${itemIndex}`} className="collapse collapse-arrow bg-base-100 border border-base-300 rounded-md">
                      <input type="checkbox" className="peer" />
                      <div className="collapse-title text-sm font-medium text-primary peer-checked:bg-primary/10 peer-checked:text-primary-content">
                         {item.item_title} ({needsUpdateEntries.length} updates)
                      </div>
                      <div className="collapse-content bg-base-100 peer-checked:bg-base-100">
                        <ul className="space-y-1 pt-2">
                          {needsUpdateEntries.map((entry, entryIndex) => (
                            <li
                              key={`update-entry-${itemIndex}-${entryIndex}`}
                              className="flex items-start gap-2 text-xs"
                            >
                              <Dot className="h-4 w-4 flex-shrink-0 text-base-content/60 mt-0.5" />
                              <span className="flex-grow text-base-content/80">
                                {entry.article_title}
                                {entry.article_url && (
                                  <a
                                    href={entry.article_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link link-info link-hover inline-flex items-center text-xs ml-1.5"
                                    title="View Article"
                                  >
                                    <Link2 className="h-3 w-3" />
                                  </a>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-3 text-right">
                           <Link to={`/release-notes/${noteId}/item/${encodeURIComponent(item.item_title)}`} className="btn btn-xs btn-outline btn-primary">
                               View Details
                           </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* End Right Column */}

      {/* Case where there are no items in the release note */}
      {noteDetails.items.length === 0 && !loading && (
        <div className="w-full md:col-span-3">
            <div className="card bg-base-200 shadow-md">
                <div className="card-body items-center text-center p-8">
                    <HelpCircle className="h-12 w-12 text-base-content/40 mb-4" />
                    <p className="text-base-content/70">
                      This release note does not contain any items that were
                      analyzed.
                    </p>
                    <div className="card-actions justify-center mt-4">
                      <Link to="/release-notes" className="btn btn-sm btn-outline">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Release Notes List
                      </Link>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default ReleaseNote;
